import React, { useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyDocument() {
  const [docId, setDocId] = useState("");
  const [hash, setHash] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const verify = async () => {
    if (!docId.trim() || hash.replace(/[^a-fA-F0-9]/g, "").length < 16) {
      setResult({ valid: false, reason: "Enter the Doc ID and at least the first 16 characters of the SHA-256 hash (both printed on the letter's audit-trail line)." });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/verify-document`, { params: { doc_id: docId.trim(), hash: hash.trim() } });
      setResult(data);
    } catch (e) {
      setResult({ valid: false, reason: e.response?.data?.detail || "Verification service unavailable — try again" });
    }
    setBusy(false);
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

  return (
    <div className="min-h-screen bg-[#101812] flex items-center justify-center p-4" data-testid="verify-document-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="font-heading text-2xl font-black text-[#EAF3EE]">LAND DRAFTERS <span className="text-[#D4AF37]">INDIA PVT. LTD.</span></div>
          <div className="text-[11px] font-mono text-[#9db3a4] mt-1 tracking-widest uppercase">Document Authenticity Verification</div>
        </div>
        <div className="bg-white border-2 border-zinc-900 rounded-sm p-5 shadow-[6px_6px_0_#D4AF37]">
          <p className="text-xs font-mono text-zinc-600 mb-4">
            Every digitally signed LAND DRAFTERS letter carries a <b>Doc ID</b> and a <b>SHA-256</b> code in its
            audit-trail line. Enter both below to confirm the document is genuine and untampered.
          </p>
          <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase">Doc ID</label>
          <input
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            placeholder="hrdoc_xxxxxxxxxxxx"
            className="w-full border-2 border-zinc-900 rounded-sm px-3 py-2 text-sm font-mono mb-3 mt-1"
            data-testid="verify-doc-id"
          />
          <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase">SHA-256 (first 16+ characters)</label>
          <input
            value={hash}
            onChange={(e) => setHash(e.target.value)}
            placeholder="e.g. 948e8816a8b129d7…"
            className="w-full border-2 border-zinc-900 rounded-sm px-3 py-2 text-sm font-mono mb-4 mt-1"
            data-testid="verify-hash"
          />
          <button
            onClick={verify}
            disabled={busy}
            className="w-full bg-[#164A30] text-white font-mono font-bold text-sm py-2.5 rounded-sm border-2 border-zinc-900 hover:bg-[#1d5f3e] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="verify-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} VERIFY DOCUMENT
          </button>

          {result && (
            <div
              className={`mt-4 border-2 rounded-sm p-4 ${result.valid ? "border-emerald-600 bg-emerald-50" : "border-rose-600 bg-rose-50"}`}
              data-testid="verify-result"
            >
              {result.valid ? (
                <>
                  <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm mb-2">
                    <ShieldCheck className="w-5 h-5" /> GENUINE DOCUMENT
                  </div>
                  <div className="text-xs font-mono space-y-1 text-zinc-700">
                    <div><b>Issuer:</b> {result.issuer}</div>
                    <div><b>Type:</b> {result.document_type}</div>
                    <div><b>Issued to:</b> {result.employee}</div>
                    {result.file_no && <div><b>File No:</b> {result.file_no}</div>}
                    <div><b>Signed by employee:</b> {fmt(result.signed_by_employee_at)}</div>
                    <div><b>Signed by HR:</b> {fmt(result.signed_by_hr_at)}</div>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2 text-rose-700 text-sm">
                  <ShieldX className="w-5 h-5 shrink-0" />
                  <div><b>NOT VERIFIED.</b> <span className="text-xs font-mono">{result.reason}</span></div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="text-center text-[10px] font-mono text-[#9db3a4] mt-4">
          Questions? Contact hr@landdrafters.com
        </div>
      </div>
    </div>
  );
}
